import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="session-timeout"
export default class extends Controller {
  static values = {
    // Durée totale de la session avant avertissement, en SECONDES.
    duration: Number,
    // Durée de la période de grâce avant déconnexion, en SECONDES.
    gracePeriod: Number,
    // URL vers laquelle rediriger pour la déconnexion.
    logoutPath: String
  }

  static targets = [
    // L'élément dans la modale où afficher le compte à rebours.
    "tempsRestant"
  ]

  connect() {
    console.log("SessionTimeoutController connected")
    this.isLoggingOut = false // État pour suivre et verrouiller le processus de déconnexion

    // Initialiser le BroadcastChannel pour la synchronisation multi-onglets
    this.channel = new BroadcastChannel('sunzen_session')
    this.channel.onmessage = this.handleChannelMessage.bind(this)
    
    // Attacher les écouteurs d'événements pour l'activité utilisateur
    this.addActivityListeners()
    
    // Lancer le minuteur principal
    this.startMainTimer()
  }

  disconnect() {
    console.log("SessionTimeoutController disconnected")
    
    // Nettoyer les minuteurs
    this.clearTimeouts()
    
    // Fermer le BroadcastChannel
    if (this.channel) {
      this.channel.close()
    }
    
    // Retirer les écouteurs d'événements
    this.removeActivityListeners()
  }

  handleChannelMessage(event) {
    if (this.isLoggingOut) return // Garde : Ne rien faire si la déconnexion est en cours

    const { data } = event
    
    switch (data.type || data) {
      case 'activity_detected':
        this.resetMainTimer()
        break
      case 'logout_now':
        // Déconnexion locale sans re-diffuser le message. Cet onglet n'est pas l'initiateur.
        this.performLogout(false)
        break
    }
  }

  handleActivity() {
    // Ne pas gérer l'activité si la déconnexion est en cours ou si l'événement est throttled
    if (this.isLoggingOut || this.activityThrottled) return

    this.activityThrottled = true
    
    // Réinitialiser le minuteur principal dans cet onglet
    this.resetMainTimer()
    
    // Diffuser l'activité aux autres onglets
    this.channel.postMessage('activity_detected')
    
    // Throttle pour éviter trop d'événements
    setTimeout(() => {
      this.activityThrottled = false
    }, 1000) // Throttle à 1 seconde
  }

  resetMainTimer() {
    if (this.isLoggingOut) return // Garde
    console.log("Resetting main timer")
    
    // Annuler le minuteur principal en cours
    this.clearTimeouts()
    
    // Si la modale d'avertissement est visible, la cacher
    this.hideWarningModal()
    
    // Relancer un nouveau minuteur principal (durée en millisecondes)
    this.mainTimer = setTimeout(() => {
      this.showWarningModal()
    }, this.durationValue * 1000)
  }

  startMainTimer() {
    this.resetMainTimer()
  }

  showWarningModal() {
    if (this.isLoggingOut) return // Garde
    console.log("Showing warning modal")
    
    const modalContainer = document.querySelector('[data-session-timeout-modal]')
    if (!modalContainer) {
      console.error('Session timeout modal not found in DOM')
      return
    }

    modalContainer.style.display = 'block'
    const dialog = modalContainer.querySelector('dialog')
    if (dialog && !dialog.open) {
      dialog.showModal()
    }
    
    this.startGracePeriodTimer()
  }

  hideWarningModal() {
    const modalContainer = document.querySelector('[data-session-timeout-modal]')
    if (modalContainer) {
      const dialog = modalContainer.querySelector('dialog')
      if (dialog && dialog.open) {
        dialog.close()
      }
      modalContainer.style.display = 'none'
    }
    
    if (this.graceTimer) {
      clearInterval(this.graceTimer)
      this.graceTimer = null
    }
  }

  startGracePeriodTimer() {
    console.log("Starting grace period timer")
    
    this.graceTimeRemaining = this.gracePeriodValue
    this.updateCountdownDisplay()
    
    this.graceTimer = setInterval(() => {
      this.graceTimeRemaining--
      this.updateCountdownDisplay()
      
      if (this.graceTimeRemaining <= 0) {
        clearInterval(this.graceTimer)
        this.graceTimer = null
        this.logout()
      }
    }, 1000)
  }

  updateCountdownDisplay() {
    if (this.hasTempsRestantTarget) {
      this.tempsRestantTarget.textContent = this.graceTimeRemaining
    }
  }

  logout() {
    if (this.isLoggingOut) return // Garde
    console.log("Logging out")
    
    // Diffuser un message de déconnexion aux autres onglets
    this.channel.postMessage({ type: 'logout_now' })
    
    // Effectuer la déconnexion locale (cet onglet est l'initiateur)
    this.performLogout(true)
  }

  performLogout(isInitiator = false) {
    // Si déjà en cours, ne rien faire
    if (this.isLoggingOut) return

    console.log("Performing logout", { isInitiator })
    this.isLoggingOut = true // Verrouiller !

    this.hideWarningModal()
    
    // L'ACTION EST LA MÊME POUR TOUS LES ONGLETS : REDIRECTION.
    // Cela annule toutes les requêtes en attente et évite les erreurs CSRF.
    window.location.href = this.logoutPathValue
  }

  clearTimeouts() {
    if (this.mainTimer) {
      clearTimeout(this.mainTimer)
      this.mainTimer = null
    }
    if (this.graceTimer) {
      clearInterval(this.graceTimer)
      this.graceTimer = null
    }
  }

  addActivityListeners() {
    this.activityEvents = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart']
    // Binder la fonction une seule fois pour pouvoir la retirer correctement
    this.boundHandleActivity = this.handleActivity.bind(this)
    
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.boundHandleActivity, true)
    })
  }

  removeActivityListeners() {
    if (this.activityEvents && this.boundHandleActivity) {
      this.activityEvents.forEach(event => {
        document.removeEventListener(event, this.boundHandleActivity, true)
      })
    }
  }
}